import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink =
      productCard?.getProductCardLink?.() ||
      hotspotProduct?.getHotspotProductLink?.() ||
      /** @type {HTMLAnchorElement | null} */ (
        productCard?.querySelector('a[ref="productCardLink"], a.product__media-link, a.product-card__link')
      );

    const href = productLink?.href || this.dataset.productUrl || '';
    if (!href) return '';

    const url = new URL(href, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      // Piece card owns color/size — skip Horizon picker re-morph. After cart
      // clears, morphing the picker again left empty size options on reopen.
      const hasPieceCard = Boolean(
        document.getElementById('quick-add-modal-content')?.querySelector('[data-bundle-inline]')
      );
      if (!hasPieceCard) {
        this.#updateVariantPicker(freshContent);
      }
    }

    this.#openQuickAddModal();
    // Ensure Piece color/size UI binds after modal open (cart clear + reopen).
    if (typeof window.imanuelReinitBundle === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => window.imanuelReinitBundle());
      });
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener(
      'animationstart',
      () => {
        this.#resetScroll();
        QuickAddComponent.enableQuickAddGalleries(dialog);
      },
      { once: true }
    );
    // Fallback if animation doesn't fire (reduced motion / CSS disabled)
    requestAnimationFrame(() => QuickAddComponent.enableQuickAddGalleries(dialog));
    setTimeout(() => QuickAddComponent.enableQuickAddGalleries(dialog), 250);
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Slim quick-add to: swipeable media + title/price + colors/sizes + bundles + view product.
   * Strips PDP chrome (FAQ “When will I get my order?”, description, etc.).
   * @param {Element} productGrid
   */
  #slimQuickAddContent(productGrid) {
    const details = productGrid.querySelector('.product-details');
    if (!details) return;

    const keepSelectors = [
      '.view-product-title',
      'product-price',
      'variant-picker',
      '[data-sizechart]',
      '.buy-buttons-block',
      '[data-bundle-inline]',
      '.imanuel-bundle-inline',
    ];

    const keepRoots = new Set();
    keepSelectors.forEach((sel) => {
      details.querySelectorAll(sel).forEach((el) => {
        // Prefer the outermost shop-control wrappers that are still under details
        keepRoots.add(el);
      });
    });

    // Also keep a compact title heading if present (first h1/h2)
    const heading = details.querySelector('h1, h2');
    if (heading) keepRoots.add(heading.closest('.text-block, .group-block') || heading);

    // Drop PDP chrome + review stars (SVG <use href="#star"> blows up without
    // block styles — shows as giant black stars on products that have ratings).
    const alwaysDrop = [
      'accordion-custom',
      '.accordion',
      'details.accordion',
      '.product-description',
      '.rating-wrapper',
      '.rating',
      '#judgeme_product_reviews',
      '.jdgm-widget',
      '.jdgm-preview-badge',
      '[class*="shipping"]',
      '[class*="delivery"]',
    ];
    alwaysDrop.forEach((sel) => {
      details.querySelectorAll(sel).forEach((el) => el.remove());
    });

    // Remove leaf blocks that aren't shop controls
    details.querySelectorAll('.text-block, .group-block, .accordion-block, product-custom-property').forEach((el) => {
      if (keepRoots.has(el)) return;
      if (Array.from(keepRoots).some((k) => el.contains(k) || k.contains(el))) return;
      // Nested empty wrappers left behind are cleaned later
      const hasKeep = keepSelectors.some((sel) => el.querySelector(sel));
      if (hasKeep) return;
      // Price/title text blocks: keep short product title/price only
      if (el.matches?.('.text-block') && el.querySelector('h1, h2, product-price')) return;
      el.remove();
    });

    // Kill FAQ leftovers by heading text
    details.querySelectorAll('h3, h4, summary, .accordion__title, .accordion-block').forEach((el) => {
      const t = (el.textContent || '').toLowerCase();
      if (t.includes('when will') || t.includes('order') || t.includes('shipping') || t.includes('delivery') || t.includes('return')) {
        (el.closest('accordion-custom, .accordion, details, .accordion-block, .group-block') || el).remove();
      }
    });

    // Reviews sit in the same group as price — strip leftovers after keep pass
    details.querySelectorAll('.rating-wrapper, .rating, #judgeme_product_reviews, .jdgm-widget, .jdgm-preview-badge').forEach((el) => {
      el.remove();
    });

    productGrid.classList.add('quick-add-modal__content--slim');
  }

  /**
   * Enable swipe on product galleries inside the quick-add dialog.
   * Horizon leaves slideshows at overflow:hidden until [in-viewport] —
   * IntersectionObserver often misses dialog (top-layer) mounts.
   * @param {ParentNode} [root]
   */
  static enableQuickAddGalleries(root = document) {
    const run = (scope) => {
      if (!scope?.querySelectorAll) return;
      scope.querySelectorAll('slideshow-component').forEach((el) => {
        el.setAttribute('in-viewport', '');
        el.removeAttribute('disabled');
        el.removeAttribute('mobile-disabled');
        el.querySelectorAll('slideshow-slide').forEach((slide) => {
          slide.removeAttribute('hidden');
          slide.style.contentVisibility = 'visible';
        });
        // Dialog top-layer often skips IntersectionObserver / lazy load — force paint
        el.querySelectorAll('img').forEach((img) => {
          if (!(img instanceof HTMLImageElement)) return;
          img.loading = 'eager';
          img.fetchPriority = 'high';
          img.style.contentVisibility = 'visible';
          if (img.dataset.src && !img.getAttribute('src')) {
            img.src = img.dataset.src;
          }
          // Kick decode for already-cached srcs that stayed unloaded
          if (typeof img.decode === 'function') {
            img.decode().catch(() => {});
          }
        });
        const slides = el.querySelector('slideshow-slides');
        if (slides instanceof HTMLElement) {
          slides.style.overflowX = 'auto';
          slides.style.flexDirection = 'row';
          slides.style.touchAction = 'pan-x';
        }
      });
    };
    run(root);
    run(document.getElementById('quick-add-dialog'));
    run(document.querySelector('dialog.quick-add-modal[open]'));
  }

  /**
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#slimQuickAddContent(productGrid);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    QuickAddComponent.enableQuickAddGalleries(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    // Piece card owns options — don't fire Horizon change (async section morph
    // emptied size buttons after remove-from-cart + reopen).
    if (modalContent.querySelector('[data-bundle-inline]')) return;

    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#enableGalleriesAfterVariantUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #enableGalleriesAfterVariantUpdate = () => {
    requestAnimationFrame(() => QuickAddComponent.enableQuickAddGalleries(this));
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
    // Toast is fired from document listener in imanuel-handoff.js
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    /* Clean size chart nodes left on the dialog host so the next open stays fresh */
    this.querySelectorAll(':scope > [data-sizechart-drawer], :scope > [data-sizechart-overlay]').forEach((el) => {
      el.remove();
    });
    const dialog = this.refs?.dialog;
    dialog?.querySelectorAll(':scope > [data-sizechart-drawer], :scope > [data-sizechart-overlay]').forEach((el) => {
      el.remove();
    });

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
